/**
 * Which model wrote a reply: "Balanced · Gemini 3.7 Flash" when a slider stop
 * chose it, the model's name when a person picked it, with a brain when
 * thinking was on.
 */
import { Brain } from "lucide-react";
import type { FC } from "react";

import { DEFAULT_AUTO_MODES } from "@/features/agents/components/ModelSelect";
import type { ModelUsed } from "@/features/agents/types";
import { cn } from "@/lib/utils";

import type { CaseAgentUIMessage } from "../useCaseAgentChat";

type Part = CaseAgentUIMessage["parts"][number];
type ModelPart = { type: "data-model"; id?: string; data: ModelUsed };

const isModelPart = (p: Part): p is ModelPart => p.type === "data-model";

/**
 * The model that ran an assistant message: from history (`metadata.model`,
 * stamped when the turn was saved) or the live `data-model` part.
 */
export const modelUsedOf = (
  message: Pick<CaseAgentUIMessage, "parts" | "metadata">
): ModelUsed | null => {
  const fromMeta = message.metadata?.model;
  if (fromMeta?.label) return fromMeta;
  const part = message.parts.find(isModelPart);
  return part?.data?.label ? part.data : null;
};

const stopLabel = (used: ModelUsed): string =>
  DEFAULT_AUTO_MODES.find((m) => m.id === used.optimize_for)?.label ?? "Auto";

/**
 * "Balanced · Gemini 3.7 Flash" (a slider stop) or the model's name. A
 * fallback says why: "Answered by Gemini 3.8 Flash because GPT-6.1 Sol timed out".
 */
export const modelUsedCopy = (used: ModelUsed): string => {
  if (used.fallback && used.explanation) return used.explanation;
  const name = used.fallback ? `${used.label} (fallback)` : used.label;
  return used.routed ? `${stopLabel(used)} · ${name}` : name;
};

export const ModelUsedLabel: FC<{
  used: ModelUsed;
  className?: string;
}> = ({ used, className }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 text-sm text-fg-3",
      className,
      used.drafted && "text-warn"
    )}
    data-testid="model-used"
    data-model-id={used.id}
    data-routed={used.routed ? "true" : undefined}
    data-fallback={used.fallback ? used.fallback_reason ?? "error" : undefined}
    title={
      used.fallback
        ? `${used.label} answered after the picked model failed`
        : used.routed
        ? `The ${stopLabel(used)} setting runs this model`
        : "The model you picked"
    }
  >
    {modelUsedCopy(used)}
    {used.thinking ? (
      <Brain className="size-3" aria-label="Thinking was on" />
    ) : null}
  </span>
);
